import React, { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useDominantColor from '../../../hooks/useDominantColor';
import MediaViewer from '../../../components/common/MediaViewer';
import LinkPreview from '../../../components/common/LinkPreview';
import Post from './Post.jsx';
import { useFeedRefresh } from '../../../features/feed';
import { IconAlertTriangle, IconExternalLink, IconUserCircle } from '@tabler/icons-react';
import Button from '../../../components/ui/Button';
import Skeleton from '../../../components/ui/Skeleton';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { formatDistanceToNow } from 'date-fns';

/**
 * Recursively resolve to the deepest original post for legacy nested reshares.
 * Posts created before Issue #9 fix may have nested sharedPostData.sharedPostData.
 */
const resolveOriginalPost = (data, id) => {
    if (data?.type === 'shared_post' && data?.sharedPostData && data?.sharedPostId) {
        return resolveOriginalPost(data.sharedPostData, data.sharedPostId);
    }
    return { resolvedData: data, resolvedId: id };
};

// Removed MAX_CONTENT_LENGTH - using visual overflow detection instead

const formatTimestamp = (timestamp) => {
    if (!timestamp) {
        return 'just now';
    }

    if (typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
        return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
    }

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
        return 'just now';
    }

    return formatDistanceToNow(date, { addSuffix: true });
};

function GridImage({ src, alt, index, onClick }) {
    const { color, ready } = useDominantColor(src);
    const [loaded, setLoaded] = useState(false);
    return (
        <div style={{ position: 'relative' }}>
            {!loaded && (
                ready ? (
                    <div className="img-dominant--grid" style={{ backgroundColor: color }} aria-hidden="true" />
                ) : (
                    <Skeleton className="absolute inset-0 w-full h-full rounded-lg" />
                )
            )}
            <img
                src={src}
                alt={alt}
                className="post__image--grid"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 768px) 48vw, 300px"
                onLoad={() => setLoaded(true)}
                onError={() => setLoaded(true)}
                onClick={() => onClick(index)}
            />
        </div>
    );
}

export default function PostContent({
    title,
    imageUrl,
    imageUrls,
    content,
    linkPreview,
    showImageModal,
    setShowImageModal,
    showFullContent,
    setShowFullContent,
    highlightTokens = [],
    sharedPostData,
    sharedPostId,
    type,
    linkPreviewData = null, // Stored preview from post creation
    isLinkPost = false,
    isInsideReshare = false,
    username,
    profession,
    timestamp,
    avatar
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeIndex, setActiveIndex] = useState(0);
    const [singleRatioClass, setSingleRatioClass] = useState('post__media--16by9');
    const [singleLoaded, setSingleLoaded] = useState(false);
    const contentRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const images = (imageUrls && imageUrls.length > 0) ? imageUrls : (imageUrl ? [imageUrl] : []);
    const { color: singleColor, ready: singleColorReady } = useDominantColor(images.length === 1 ? images[0] : null);
    const { setPriorityPost } = useFeedRefresh();
    const { currentUser } = useAuth();

    // Use stored link preview data if available, otherwise fall back to dynamically fetched
    const effectiveLinkPreview = linkPreviewData || linkPreview;

    // Detect if content is visually truncated (overflows the collapsed height)
    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el || showFullContent) {
            // If expanded, don't change overflow state
            return;
        }
        // Check if the scrollHeight exceeds the clientHeight (meaning it's truncated)
        // Add a 2px buffer to prevent false positives due to sub-pixel rendering.
        const isTruncated = el.scrollHeight > el.clientHeight + 2;
        setIsOverflowing(isTruncated);
    }, [content, showFullContent]);

    // Resolve to deepest original post for legacy nested reshares
    const { resolvedData, resolvedId } = useMemo(() => {
        return type === 'shared_post'
            ? resolveOriginalPost(sharedPostData, sharedPostId)
            : { resolvedData: null, resolvedId: null };
    }, [type, sharedPostData, sharedPostId]);

    // Handle click on shared/reshared post to navigate to original
    const handleSharedPostClick = useCallback((e) => {
        if (isInsideReshare) return; // Disable navigation in previews
        if (!currentUser) {
            toast.info("Please log in to view this post");
            return;
        }
        const postId = resolvedId || sharedPostData?.id;
        if (postId) {
            // Set as priority post (top of feed with highlight) using the locally cached snapshot if available
            setPriorityPost(postId, resolvedData || sharedPostData);
            if (location.pathname !== '/home') {
                navigate(`/home?post=${postId}`, { state: { scrollToPost: postId } });
            } else {
                navigate(`/home?post=${postId}`, { replace: true });
            }
        }
    }, [isInsideReshare, resolvedId, sharedPostData, setPriorityPost, location.pathname, navigate, currentUser, resolvedData, sharedPostId]);

    const suppressReopenWindowMs = 350;
    const shouldSuppressOpen = () => {
        try {
            const t = window.__mediaViewerJustClosedAt || 0;
            return Date.now() - t < suppressReopenWindowMs;
        } catch { return false; }
    };
    const openMediaAt = (index) => {
        if (shouldSuppressOpen()) return;
        setActiveIndex(index);
        setShowImageModal(true);
    };
    const closeMedia = () => {
        try { window.__mediaViewerJustClosedAt = Date.now(); } catch { }
        setShowImageModal(false);
    };
    const buildAlt = (fallback, idx = null) => {
        const base = (title && String(title).trim()) || (content && String(content).slice(0, 80)) || fallback;
        return idx != null ? `${base} (${idx + 1})` : base;
    };
    const renderHighlighted = (text) => {
        if (!text) return '';
        const tokens = Array.isArray(highlightTokens) ? highlightTokens.filter(Boolean) : [];
        if (!tokens.length) return String(text);
        const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'gi');
        const parts = String(text).split(pattern);
        return parts.map((part, idx) => (
            idx % 2 === 1 ? <mark key={idx} className="hl-mark">{part}</mark> : <span key={idx}>{part}</span>
        ));
    };
    return (
        <div className="post__content">
            {/* Regular post: title + content */}
            {type !== 'shared_post' && (
                <>
                    {title && (
                        <h2 className="post__title">{renderHighlighted(title)}</h2>
                    )}
                    <p
                        ref={contentRef}
                        className={showFullContent && isOverflowing ? 'expanded' : 'collapsed'}
                    >
                        {renderHighlighted(content)}
                    </p>
                    {isOverflowing && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-1 w-full text-primary flex justify-center hover:bg-primary/5"
                            onClick={() => setShowFullContent(!showFullContent)}
                        >
                            {showFullContent ? 'Show less' : 'Read more'}
                        </Button>
                    )}
                </>
            )}

            {/* Shared Post: commentary on top, original post in middle, view link at bottom */}
            {type === 'shared_post' && (
                resolvedData && resolvedId ? (
                    <>
                        {/* Resharer's commentary (text only, identity shown in outer post header) */}
                        {content && (
                            <div className="reshare-commentary mb-4">
                                <p className="text-sm font-medium text-gray-800 leading-relaxed whitespace-pre-wrap break-words">{content}</p>
                            </div>
                        )}

                        <div className="reshare-inner-post mt-3 rounded-2xl p-4 bg-white relative block w-full text-left">
                            {/* Original post (same layout as standalone post) */}
                            <Post
                                key={`inner-${resolvedId}`}
                                {...resolvedData}
                                profession={resolvedData.userProfession || resolvedData.profession || ''}
                                avatar={resolvedData.userPhotoURL || resolvedData.avatar || ''}
                                title={resolvedData.title ? resolvedData.title.replace(/^\s*Shared:\s*/i, '') : ''}
                                postId={resolvedId}
                                hideFooter={true}
                                preventAutoScroll={true}
                                isInsideReshare={true}
                                actionTag={resolvedData.actionTag || ''}
                                type="post"
                                highlightTokens={[]}
                            />

                            {/* View original post link below */}
                            <button
                                type="button"
                                className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 text-blue-500 hover:text-blue-700 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                                onClick={handleSharedPostClick}
                                aria-label="View original post"
                            >
                                <IconExternalLink size={14} />
                                <span>View Original Post</span>
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-center gap-2 p-6 mt-3 bg-gray-50 border border-dashed border-gray-300 rounded-xl text-gray-500 text-sm [&_svg]:text-gray-400 [&_svg]:shrink-0">
                        <IconAlertTriangle size={20} />
                        <span>Original post is no longer available</span>
                    </div>
                )
            )}

            {(() => {
                if (images.length === 0) return null;
                return (
                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                        {images.length === 1 ? (
                            // Single image display (Facebook-like contained)
                            <div
                                className={`post__media ${singleRatioClass}`}
                                onClick={() => openMediaAt(0)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { openMediaAt(0); } }}
                            >
                                {!singleLoaded && (
                                    singleColorReady
                                        ? <div className="img-dominant" style={{ backgroundColor: singleColor }} aria-hidden="true" />
                                        : <Skeleton className="absolute inset-0 w-full h-full" />
                                )}
                                <img
                                    src={images[0]}
                                    alt={buildAlt('Post image')}
                                    className="post__image"
                                    loading="lazy"
                                    sizes="(max-width: 768px) 100vw, 600px"
                                    onLoad={(e) => {
                                        const w = e.currentTarget.naturalWidth || 1;
                                        const h = e.currentTarget.naturalHeight || 1;
                                        const r = w / h;
                                        if (r >= 1.2) setSingleRatioClass('post__media--16by9');
                                        else if (r <= 0.9) setSingleRatioClass('post__media--4by5');
                                        else setSingleRatioClass('post__media--1by1');
                                        setSingleLoaded(true);
                                    }}
                                    onError={() => setSingleLoaded(true)}
                                    decoding="async"
                                />
                            </div>
                        ) : (
                            // Multiple images grid with modern layout
                            <div className="post__images-grid" data-count={Math.min(images.length, 4)}>
                                {images.slice(0, 4).map((img, index) => (
                                    <div key={index} style={{ position: 'relative' }}>
                                        <GridImage
                                            src={img}
                                            alt={buildAlt('Post image', index)}
                                            index={index}
                                            onClick={(i) => openMediaAt(i)}
                                        />
                                        {index === 3 && images.length > 4 && (
                                            <div className="post__image--more-overlay visible">
                                                +{images.length - 4}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {showImageModal && (
                            <MediaViewer
                                mediaUrls={images}
                                mediaType="image"
                                initialIndex={activeIndex}
                                onClose={closeMedia}
                            />
                        )}
                    </div>
                );
            })()}

            {effectiveLinkPreview && ( // Render LinkPreview if metadata is available
                <LinkPreview
                    url={effectiveLinkPreview.url}
                    title={effectiveLinkPreview.title}
                    description={effectiveLinkPreview.description}
                    image={effectiveLinkPreview.image}
                />
            )}
        </div>
    );
}